import React from "react";
import { ExternalLink, PlusCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    formatTotal,
    calculateDiscountedPrice,
    getProductAvailability,
} from "@/lib/utils";

// ----------------------------------------------------------------------------

interface ProductResultItemProps {
    code: string;
    name?: string;
    price: string;
    numericPrice: number;
    discountPercent?: number;
    imageUrl?: string;
    tags?: string[];
    stockCount?: number;
    unlimited?: number;
    onAdd: () => void;
    className?: string;
}

export function ProductResultItem(props: ProductResultItemProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const {
        code,
        name,
        price,
        numericPrice,
        discountPercent,
        imageUrl,
        tags,
        stockCount,
        unlimited,
        onAdd,
        className,
    } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [imageError, setImageError] = React.useState(false);

    // -------------------------------------
    // Effects
    // -------------------------------------

    React.useEffect(() => {
        setImageError(false);
    }, [imageUrl, code]);

    // -------------------------------------
    // Component functions
    // -------------------------------------

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const availability = React.useMemo(
        () => getProductAvailability({ tags, stockCount, unlimited }),
        [tags, stockCount, unlimited],
    );

    const hasDiscount = discountPercent !== undefined && discountPercent > 0;
    const finalUnitPrice = hasDiscount
        ? calculateDiscountedPrice(numericPrice, discountPercent)
        : numericPrice;
    const formattedFinalPrice = formatTotal(finalUnitPrice);

    return (
        <div
            className={`flex flex-col gap-3 p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors ${className || ""}`}>
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                    {imageUrl && !imageError ? (
                        <img
                            src={imageUrl}
                            alt={code}
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-400">
                            N/D
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 min-w-0 flex-1">
                    <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-base truncate">
                            {code}
                        </p>
                        {name && name !== code && (
                            <p className="text-xs text-gray-500 truncate -mt-0.5 font-medium">
                                {name}
                            </p>
                        )}
                        {/* Availability Badge */}
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span
                                className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-semibold border ${
                                    availability.caseType === "available"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        : availability.caseType === "restocking"
                                          ? "bg-amber-50 text-amber-700 border-amber-200"
                                          : availability.caseType ===
                                              "out_of_catalog_with_stock"
                                            ? "bg-orange-50 text-orange-700 border-orange-200"
                                            : "bg-red-50 text-red-700 border-red-200"
                                }`}>
                                <span
                                    className={`w-1 h-1 rounded-full ${
                                        availability.caseType === "available"
                                            ? "bg-emerald-600"
                                            : availability.caseType ===
                                                "restocking"
                                              ? "bg-amber-600"
                                              : availability.caseType ===
                                                  "out_of_catalog_with_stock"
                                                ? "bg-orange-600"
                                                : "bg-red-600"
                                    }`}
                                />
                                {availability.caseType === "restocking" &&
                                availability.restockDate
                                    ? `In riassortimento - ${availability.restockDate}`
                                    : availability.statusLabel}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col text-right shrink-0">
                        {hasDiscount ? (
                            <>
                                <div className="flex items-center justify-end gap-1.5">
                                    <span className="text-[11px] text-gray-400 line-through">
                                        {price}
                                    </span>
                                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200">
                                        -{discountPercent}%
                                    </span>
                                </div>
                                <span className="font-bold text-gray-900 text-sm">
                                    {formattedFinalPrice}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="text-[10px] text-gray-500 leading-none mb-0.5">
                                    a partire da
                                </span>
                                <span className="font-bold text-gray-900 text-sm">
                                    {price}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
                <Button
                    size="sm"
                    disabled={!availability.canOrder}
                    className={`flex-1 sm:flex-none rounded-md h-9 px-3 text-xs flex items-center justify-center gap-1.5 min-w-0 ${
                        availability.canOrder
                            ? "bg-[#0D6EFD] hover:bg-blue-700 text-white"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                    }`}
                    onClick={(e) => {
                        // Prevent closing the dropdown if clicked
                        e.preventDefault();
                        e.stopPropagation();
                        if (availability.canOrder) {
                            onAdd();
                        }
                    }}
                    onMouseDown={(e) => {
                        // Prevent the input's onBlur from firing before onClick
                        e.preventDefault();
                    }}>
                    <PlusCircle className="w-3.5 h-3.5" />
                    Aggiungi prodotto
                </Button>
            </div>
        </div>
    );
}
